import { useRef } from 'react';
import { useLocation } from 'react-router';

/** The enquiry funnel stages that fire at most once per page view. */
export type EnquiryFunnelStage = 'reveal' | 'engagement' | 'formEngagement';

/**
 * Owns the one-shot state for the inline enquiry funnel.
 *
 * The form lives in an accordion row that Radix unmounts when closed, so the
 * fired-stage set cannot live in the form itself — reopening the row would
 * refire the funnel. It has to be owned by an ancestor, but an ancestor's bare
 * ref lives too long: React keeps the route component mounted across same-route
 * navigations (article → article via Discover More, project → project), so the
 * set would carry over and silence the funnel on the next page.
 *
 * Keying on the history entry alone does not work either — `location.key` is
 * reused on browser back/forward.
 *
 * So the set resets whenever the *page view* changes, identified by the history
 * entry plus the item being enquired about. Comparing against the immediately
 * previous token (rather than a set of every token seen) is what makes
 * back/forward work: returning to article A from article B is a different token
 * than the one in hand, so it resets and the funnel fires again.
 */
export const useEnquiryFunnelStages = (
    itemType: string,
    siteTreeID: number,
): Set<EnquiryFunnelStage> => {
    const location = useLocation();
    const token = `${location.key}:${itemType}:${siteTreeID}`;

    const stagesRef = useRef<Set<EnquiryFunnelStage>>(new Set());
    const tokenRef = useRef(token);

    // Derived during render, not in an effect: the form's impression can fire
    // on the same commit, and an effect would clear the set after it had
    // already recorded the reveal. Idempotent, so StrictMode's double render is
    // a no-op.
    if (tokenRef.current !== token) {
        tokenRef.current = token;
        stagesRef.current = new Set();
    }

    return stagesRef.current;
};
